import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { Store } from "@ngrx/store";
import { tap, throwError } from "rxjs";
import { Product } from "src/app/quote/term-life-insurance/services/backend/dto/enums/product.enum";
import { QuoteHttpFacadeService } from "src/app/quote/term-life-insurance/services/backend/quote-http-facade.service";
import { AppState } from "src/app/shared/store/app.state";
import * as TermLifeInsuranceQuoteActions from "src/app/shared/store/quote/term-life-insurance/quote.actions";
import { UnreachableCaseError } from "ts-essentials";

import { InvalidOperationException } from "../../../shared/exceptions/exceptions";
import { QueryStringParameterKey } from "../../../shared/routes/shared/shared-route.constant";

/**
 * This resolver is partially blocking.
 * Loading the product is blocking while afterwards loading the quote is not.
 */
export const getQuoteResolver = (
  activatedRouteSnapshot: ActivatedRouteSnapshot,
  routerStateSnapshot: RouterStateSnapshot,
) => {
  const store = inject<Store<AppState>>(Store<AppState>);
  const quoteHttpFacadeService = inject(QuoteHttpFacadeService);

  const quoteId = activatedRouteSnapshot.queryParamMap.get(
    QueryStringParameterKey.QUOTE_ID,
  );

  if (!quoteId) {
    return throwError(
      () =>
        new Error(
          `The url '${routerStateSnapshot.url}' can not be called without providing a quoteId`,
        ),
    );
  }

  return quoteHttpFacadeService.getProduct(quoteId).pipe(
    tap((productDto) =>
      // Fire & Forget
      {
        loadProductSpecificQuote(store, productDto.product, quoteId);
      },
    ),
  );
};

function loadProductSpecificQuote(
  store: Store<AppState>,
  product: Product,
  quoteId: string,
) {
  switch (product) {
    case Product.TERM_LIFE_INSURANCE_CONSTANT:
      store.dispatch(
        TermLifeInsuranceQuoteActions.getTermLifeInsuranceQuote({ quoteId }),
      );
      break;
    case Product.PILLAR_3A_CONSTANT:
      throw new InvalidOperationException(
        "We do not yet support loading pillar 3a quotes via 'getQuoteResolver'",
      );
    default:
      throw new UnreachableCaseError(product);
  }
}
